import { useMemo, useState, useEffect } from "react";
import { Stack, useTheme } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EarnWidget, PayWidget } from "src/assets";
import CertificateSection from "src/components/Common/CertificateSection";
import BoldSubtitle from "src/components/Displays/BoldSubtitle";
import PageSubTitle from "src/components/Displays/PageSubTitle";
import Text from "src/components/Displays/Text";
import TextSubtitle from "src/components/Displays/TextSubtitle";
import StartCalculatorModal from "src/components/Modal/StartCalculatorModal";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import TabActive from "src/components/TabActive/TabActive";
import { ROUTES } from "src/consts/routes";
import { useToggle } from "src/hooks/useToggle";
import CalculatedSavingsSummary from "src/pages/Tarif/components/CalculatedSavingsSummary";
import { BONUS, SESSION_KEYS } from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { Tariff } from "src/models/clientsModel";
import { Box, CircularProgress } from "@mui/material";

const Content = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useToggle(false);
  const [payButtonActive, togglePayButtonActive] = useToggle(true);
  const [earnButtonActive, toggleEarnButtonActive] = useToggle(false);

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );

  const { tariffsData } = useMemo(() => {
    const tariffsData = getSessionStorageItem();
    return { tariffsData };
  }, [getSessionStorageItem]);

  const hasTariffs = Array.isArray(tariffsData) && tariffsData.length > 0;

  const [searchParams] = useSearchParams();
  const tid = searchParams.get("tid") ?? "";
  const spid = searchParams.get("spid") ?? "";

  const isStageOrProdEnv =
    process.env.REACT_APP_BUILD_ENV === "STAGE" ||
    process.env.REACT_APP_BUILD_ENV === "PROD";

  const showOverlay =
    (tid.length === 0 || spid.length === 0) && isStageOrProdEnv;

  const handleStartCalculator = () => {
    if (showOverlay) {
      toggleModal();
      return;
    }
    navigate(ROUTES.PV_SETUP);
  };

  const handleModalSubmit = () => {
    const paPoSignUpUrl = process.env.REACT_APP_PAPO_SIGN_UP_URL;
    if (paPoSignUpUrl) window.open(paPoSignUpUrl, "_blank");
  };

  const openSummary = searchParams.get("openSummary") === "true";

  const [showSpinner, setShowSpinner] = useState(true);

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  useEffect(() => {
    if (!openSummary && tid === "") {
      setShowSpinner(false);
      return;
    }

    let latestTariffs: Tariff[] = [];

    try {
      const initialData = sessionStorage.getItem(SESSION_KEYS.TARIFFS);
      if (initialData) {
        latestTariffs = JSON.parse(initialData);
        setTariffs(latestTariffs);
      }
    } catch {}

    if (openSummary) {
      setShowSpinner(false);
      return;
    }

    const interval = setInterval(() => {
      try {
        const storage = sessionStorage.getItem(SESSION_KEYS.TARIFFS);
        if (!storage) return;

        const parsed = JSON.parse(storage) as Tariff[];

        if (JSON.stringify(parsed) !== JSON.stringify(latestTariffs)) {
          latestTariffs = parsed;
          setTariffs(parsed);
        }
      } catch {}
    }, 500);

    const timer = setTimeout(() => {
      if (latestTariffs.length > 0) {
        setShowSpinner(false);
      } else {
        setShowSpinner(false);
      }

      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [openSummary, tid, navigate, setTariffs]);

  return (
    <Stack
      gap={6}
      sx={{
        width: "100%",
        maxWidth: "880px",
      }}
    >
      {showSpinner && (
        <Box
          width="100%"
          minHeight="200px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress sx={{ color: "gray" }} />
        </Box>
      )}

      {!showSpinner && (
        <Stack
          gap={2}
          width="100%"
          alignItems={{ xs: "center", sm: "flex-start" }}
        >
          {hasTariffs && (
            <>
              <Text
                sx={{
                  color: "black",
                  fontSize: "20px",
                  fontWeight: "600",
                  lineHeight: "26px",
                  fontFamily: "isonType",
                  letterSpacing: "-0.4px",
                  mb: 4,
                }}
                text={`${t("page.calculatedSavingsSummary.title")}:`}
              />
              <Box width={{ xs: "100%", sm: "auto" }}>
                <CalculatedSavingsSummary
                  tariffsData={tariffsData}
                  showSavingsResult={true}
                  openByDefault={openSummary}
                  getLogo={() => ({ houseImg: "/path/to/img", cssProps: {} })}
                />
              </Box>
              <PrimaryButton
                onClick={() => navigate(ROUTES.TARIF)}
                sx={{
                  textTransform: "none",
                  width: { xs: "100%", sm: "28.4%" },
                  mt: 4,
                  minHeight: "50px",
                  minWidth: "250px",
                  fontSize: "18px",
                  alignSelf: "center",
                  "&:hover": {
                    opacity: 0.75,
                  },
                }}
                text={t("page.calculatedSavingsSummary.tarifDetails")}
              />
            </>
          )}

          <PrimaryButton
            onClick={() => {
              setBonus(null);
              setTariffs([]);
              handleStartCalculator();
            }}
            sx={(theme) => ({
              width: { xs: "100%", sm: "250px" },
              alignSelf: hasTariffs
                ? "center"
                : { xs: "center", sm: "flex-start" },
              textTransform: "none",
              fontSize: "18px",
              padding: hasTariffs ? "4px 12px" : undefined,
              minHeight: "50px",
              minWidth: "250px",
              mt: 2,
              border: hasTariffs ? "1px solid #000000" : "none",
              bgcolor: hasTariffs
                ? theme.palette.secondaryButton.background
                : undefined,
              color: hasTariffs
                ? theme.palette.secondaryButton.text
                : undefined,
              "&:hover": {
                bgcolor: hasTariffs
                  ? theme.palette.secondaryButton.background
                  : undefined,
                "&:hover": {
                  opacity: 0.75,
                },
              },
            })}
            text={
              hasTariffs
                ? t("page.introduction.textNewCalculation")
                : t("page.introduction.textCalculation")
            }
          />
        </Stack>
      )}

      <Stack alignItems={{ xs: "center", sm: "flex-start" }}>
        <CertificateSection />
      </Stack>

      <BoldSubtitle text={t("page.introduction.textTitle")} />
      <Stack gap={6}>
        <TextSubtitle
          sx={{
            fontSize: { xs: "16px", sm: "20px" },
            fontWeight: { xs: 500, sm: 400 },
            lineHeight: { xs: "22px", sm: "30px" },
          }}
          text={t("page.introduction.textSubtitleOne")}
        />
        <Stack>
          <TextSubtitle
            sx={{
              fontSize: { xs: "16px", sm: "20px" },
              fontWeight: { xs: 500, sm: 400 },
              lineHeight: { xs: "22px", sm: "30px" },
            }}
            text={t("page.introduction.textSubtitleTwo")}
          />
          <TextSubtitle
            sx={{
              fontSize: { xs: "16px", sm: "20px" },
              fontWeight: { xs: 500, sm: 400 },
              lineHeight: { xs: "22px", sm: "30px" },
            }}
            text={t("page.introduction.textSubtitleThree")}
          />
        </Stack>
      </Stack>
      <Stack width={"100%"} flexDirection="row" gap={{ xs: 0, sm: 6 }}>
        <TabActive
          isActive={payButtonActive}
          toggleActive={() => {
            if (payButtonActive) return;
            togglePayButtonActive();
            if (earnButtonActive) toggleEarnButtonActive();
          }}
        >
          <Text
            text={t("page.introduction.payButtonTitle")}
            sx={{
              fontWeight: 700,
              color: payButtonActive
                ? theme.palette.tabActive.text
                : theme.palette.tabInactive.text,
            }}
          />
          <PageSubTitle
            color={
              payButtonActive
                ? theme.palette.tabActive.text
                : theme.palette.tabInactive.text
            }
            text={t("page.introduction.payButtonSubtitle")}
          />
        </TabActive>

        <TabActive
          isActive={earnButtonActive}
          toggleActive={() => {
            if (earnButtonActive) return;
            toggleEarnButtonActive();
            if (payButtonActive) togglePayButtonActive();
          }}
        >
          <Text
            text={t("page.introduction.earnButtonTitle")}
            sx={{
              fontWeight: 700,
              color: earnButtonActive
                ? theme.palette.tabActive.text
                : theme.palette.tabInactive.text,
            }}
          />
          <PageSubTitle
            color={
              earnButtonActive
                ? theme.palette.tabActive.text
                : theme.palette.tabInactive.text
            }
            text={t("page.introduction.earnButtonSubtitle")}
          />
        </TabActive>
      </Stack>
      <Stack width={"100%"}>
        {earnButtonActive && (
          <EarnWidget
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        )}
        {payButtonActive && (
          <PayWidget
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        )}
      </Stack>
      <Text
        text={t("page.introductionSMA.footNote")}
        sx={{
          lineHeight: "21px",
        }}
      />
      <Text
        text={t("page.introduction.footNote2")}
        sx={{
          lineHeight: "21px",
        }}
      />
      <Text
        text={t("page.introductionSMA.footNote3")}
        sx={{
          lineHeight: "21px",
        }}
      />
      <Box
        sx={(theme) => ({
          lineHeight: "21px",
          color: theme.palette.app.secondaryFont,
          "& a": {
            color: "the",
            textDecoration: "none",
          },
          "& a:hover": {
            textDecoration: "underline",
          },
        })}
      >
        <Trans
          i18nKey="page.introductionSMA.footNote4"
          components={{
            1: (
              <a
                href="https://www.lichtblick.de/oekostrom/stromkennzeichnung/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View electricity labeling"
              />
            ),
          }}
        />
      </Box>

      <StartCalculatorModal
        isOpen={isModalOpen}
        onClose={toggleModal}
        onSubmit={handleModalSubmit}
        title={t("page.introduction.modal.title")}
        subtitle1={t("page.introduction.modal.subtitle1")}
        subtitle2={t("page.introduction.modal.subtitle2")}
        subtitle3={t("page.introduction.modal.subtitle3")}
        submitButtonText={t("page.introduction.modal.submitButtonText")}
      />
    </Stack>
  );
};

export default Content;
